import JobPanel from "@spa/features/search_job/pc/components/list/JobPanel";
import JobAlert from "@spa/features/search_job/pc/components/list/JobAlert";
import {useEffect} from "react";
import {normalizeLocation} from "@src/common/service/savedJobs";
import {Feature} from "@src/common/model/Feature";
import {addNewViewedJob, updateAll} from "@src/common/service/viewHistory";
import {useNavigate} from "react-router-dom";
import {useFocus} from "@spa/features/search_job/pc/contexts/FocusContext";
import {trackClickSearchResult, trackJobViewFirst} from "@spa/features/search_job/pc/api/analytics/trackEvent";
import {useJobSwitch} from "@spa/features/search_job/pc/contexts/JobSwitchContext";

const JobPanelList = ({
    searchResultViewModel,
    savedJobsForSearchResult,
    handleSaveJob,
}) => {
    const routerNavigate = useNavigate();
    const {focusUniqueId, setFocusUniqueId} = useFocus();
    const {activeUniqueId, jobViewModelForDetail} = useJobSwitch();

    // 1ページ目 かつ 10件以上：求人パネル中のジョブアラートも表示
    const isShowJobAlertInJob = !searchResultViewModel.hasPreviousPage && searchResultViewModel.jobViewModelList.length >= 10;

    useEffect(() => {
        postAnalyticsDataForFirstRendering();
    }, []);

    /**
     * 初回ランディング時の計測データ送信
     */
    const postAnalyticsDataForFirstRendering = () => {
        const hasInitialJobDetail = jobViewModelForDetail !== null && jobViewModelForDetail.isValid;
        // 求人詳細がない場合は計測しない
        if (!hasInitialJobDetail) return;

        let activeJobViewModel = searchResultViewModel.jobViewModelList.find(jobViewModel => jobViewModel.uniqueId === activeUniqueId);
        let activeJobViewModelKey = searchResultViewModel.jobViewModelList.findIndex(jobViewModel => jobViewModel.uniqueId === activeUniqueId);
        if (!activeJobViewModel) { // 有効な求人がない場合は1件目の求人を有効求人とする
            activeJobViewModel = searchResultViewModel.jobViewModelList[0];
            activeJobViewModelKey = 0;
        }

        // 計測
        const analyticsData = jobViewModelForDetail.analyticsViewModel;
        const location = jobViewModelForDetail.locations && jobViewModelForDetail.locations.length ? jobViewModelForDetail.locations[0] : 'not_set'; // 複数勤務地化の影響により、画面側から先頭勤務地を計測
        const postedAt = activeJobViewModel.formattedUpdateDate;
        const currentPage = searchResultViewModel.currentPage;
        const position = activeJobViewModelKey + 1;
        const isSaved = savedJobsForSearchResult[analyticsData.uniqueId];
        trackJobViewFirst(analyticsData, location, postedAt, currentPage, position, isSaved);
    }

    /**
     * パネルクリック時の求人フォーカス移動処理
     * @param jobViewModel
     * @param position
     */
    const handlePanelClick = (jobViewModel, position) => {
        // 選択済みのパネルの場合は何もしない
        if (jobViewModel.uniqueId === activeUniqueId) {
            return;
        }

        // 閲覧履歴保存
        saveViewedJob(jobViewModel);

        // jbパラメータを更新
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('jb', jobViewModel.uniqueId);
        routerNavigate(`?${searchParams.toString()}`);

        // 計測
        const analyticsData = jobViewModel.analyticsData;
        const postedAt = jobViewModel.formattedUpdateDate;
        const currentPage = searchResultViewModel.currentPage;
        const isSaved = savedJobsForSearchResult[analyticsData.uniqueId];
        trackClickSearchResult(analyticsData, postedAt, currentPage, position, isSaved);
    }

    /**
     * 閲覧履歴の登録処理
     * @param jobViewModel
     */
    const saveViewedJob = (jobViewModel) => {
        // 閲覧履歴に追加する求人データ取得
        const viewedJob = jobViewModel;
        viewedJob['isFromJobPanel'] = true;

        // 正規化対象の勤務地表記を正規化 USのNeighborhoodの場合
        if (searchResultViewModel.shouldNormalizeLocation) {
            viewedJob['location'] = normalizeLocation(viewedJob['location']);
        }

        // featureViewModelをFeatureオブジェクトに変換
        viewedJob['features'] = new Feature(viewedJob['featureViewModel']);

        // 閲覧履歴リスト取得
        const viewHistory = addNewViewedJob(viewedJob);
        // 更新
        updateAll(viewHistory);
    }

    /**
     * パネルキー押下時の処理
     * @param e
     * @param jobViewModel
     */
    const handlePanelKeyPress = (e, jobViewModel) => {
        if (e.key === 'Enter' || e.key === ' ') {
            handlePanelClick(jobViewModel);
        }
    }

    /**
     * フォーカス時の処理
     * @param jobViewModel
     */
    const handlePanelFocus = (jobViewModel) => {
        setFocusUniqueId(jobViewModel.uniqueId);
    }

    /**
     * フォーカスが外れた時の処理
     */
    const handlePanelBlur = () => {
        setFocusUniqueId(null);
    }

    if (!isShowJobAlertInJob) {
        return searchResultViewModel.jobViewModelList.map((jobViewModel, index) => {
            return (
                <JobPanel
                    key={index}
                    position={index + 1}
                    jobViewModel={jobViewModel}
                    isActive={jobViewModel.uniqueId === activeUniqueId}
                    isFocus={jobViewModel.uniqueId === focusUniqueId}
                    isSaved={savedJobsForSearchResult[jobViewModel.uniqueId]}
                    handleSaveJob={handleSaveJob}
                    onPanelClick={handlePanelClick}
                    onPanelKeyPress={handlePanelKeyPress}
                    onPanelFocus={handlePanelFocus}
                    onPanelBlur={handlePanelBlur}
                    translationsJobItems={searchResultViewModel.translations.job_items}
                />
            );
        });
    }

    const topJobPanelList = searchResultViewModel.jobViewListTop.map((jobViewModel, index) => {
        return (
            <JobPanel
                key={index}
                position={index + 1}
                jobViewModel={jobViewModel}
                isActive={jobViewModel.uniqueId === activeUniqueId}
                isFocus={jobViewModel.uniqueId === focusUniqueId}
                isSaved={savedJobsForSearchResult[jobViewModel.uniqueId]}
                handleSaveJob={handleSaveJob}
                onPanelClick={handlePanelClick}
                onPanelKeyPress={handlePanelKeyPress}
                onPanelFocus={handlePanelFocus}
                onPanelBlur={handlePanelBlur}
                translationsJobItems={searchResultViewModel.translations.job_items}
            />
        );
    });
    const bottomJobPanelList = searchResultViewModel.jobViewListBottom.map((jobViewModel, index) => {
        return (
            <JobPanel
                key={index}
                position={index + 1}
                jobViewModel={jobViewModel}
                isActive={jobViewModel.uniqueId === activeUniqueId}
                isFocus={jobViewModel.uniqueId === focusUniqueId}
                isSaved={savedJobsForSearchResult[jobViewModel.uniqueId]}
                handleSaveJob={handleSaveJob}
                onPanelClick={handlePanelClick}
                onPanelKeyPress={handlePanelKeyPress}
                onPanelFocus={handlePanelFocus}
                onPanelBlur={handlePanelBlur}
                translationsJobItems={searchResultViewModel.translations.job_items}
            />
        );
    })

    return (
        <>
            { topJobPanelList }
            {searchResultViewModel.isDisplayJobAlert && <JobAlert isInJobPanelList={true} translations={searchResultViewModel.translations} />}
            { bottomJobPanelList }
        </>
    );
}
export default JobPanelList;
